import { DataObject } from '@shared/models/data-object.model';

const UPDATE_LEASE_ARRANGEMENT = 'updateLeaseArrangement';
const ADD_LEASE_METHODS: readonly string[] = ['addSpaceLease', 'addLeaseSpaces'];
const ARRANGEMENT_KEY = 'arrangement';
const CONFIDENTIALITY_FIELD = 'confidentiality_f';
const FLAT_CONFIDENTIALITY_KEY = `${ARRANGEMENT_KEY}.${CONFIDENTIALITY_FIELD}`;
const CONFIDENTIALITY_OFF = '0';
const CONFIDENTIALITY_ON = '1';

const isPlainObject = (value: unknown): value is DataObject =>
  value != null && typeof value === 'object' && !Array.isArray(value);

/** null / boolean to the "0" / "1" string EDP expects. Any other value (e.g. "2") is returned as is. */
const toConfidentialityString = (value: unknown): unknown => {
  if (value === null) return CONFIDENTIALITY_OFF;
  if (typeof value === 'boolean') return value ? CONFIDENTIALITY_ON : CONFIDENTIALITY_OFF;
  return value;
};

/**
 * Write side: converts the lease `confidentiality_f` of an update payload to "0" / "1".
 * The caller applies it for the space entity only.
 * `updateLeaseArrangement` carries it as `arrangement.confidentiality_f`, the add-lease
 * methods carry it inside the nested `arrangement` object. Returns a copy only when a
 * conversion applies, otherwise the same payload.
 */
export function normalizeLeaseConfidentiality<T extends DataObject>(method: string | undefined, payload: T): T {
  if (!isPlainObject(payload)) return payload;
  if (method === UPDATE_LEASE_ARRANGEMENT && FLAT_CONFIDENTIALITY_KEY in payload) {
    return { ...payload, [FLAT_CONFIDENTIALITY_KEY]: toConfidentialityString(payload[FLAT_CONFIDENTIALITY_KEY]) };
  }
  const arrangement = payload[ARRANGEMENT_KEY];
  if (ADD_LEASE_METHODS.includes(method ?? '') && isPlainObject(arrangement) && CONFIDENTIALITY_FIELD in arrangement) {
    return {
      ...payload,
      [ARRANGEMENT_KEY]: { ...arrangement, [CONFIDENTIALITY_FIELD]: toConfidentialityString(arrangement[CONFIDENTIALITY_FIELD]) },
    };
  }
  return payload;
}

/**
 * Read side: shows the "0" / "1" of `leasecomps[].confidentiality_f` as false / true so the
 * Y/N boolean column displays and selects correctly. Other values ("2", null, masked) are kept.
 * Returns a new record only when a lease changes.
 */
export function leaseCompConfidentialityToBool(record: DataObject): DataObject {
  const leaseComps = record?.leasecomps;
  if (!Array.isArray(leaseComps)) return record;
  let changed = false;
  const converted = leaseComps.map(leaseComp => {
    const value = leaseComp?.[CONFIDENTIALITY_FIELD];
    if (value !== CONFIDENTIALITY_OFF && value !== CONFIDENTIALITY_ON) return leaseComp;
    changed = true;
    return { ...leaseComp, [CONFIDENTIALITY_FIELD]: value === CONFIDENTIALITY_ON };
  });
  return changed ? { ...record, leasecomps: converted } : record;
}
